import { Stack, Typography } from "@mui/material";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { divider } from "@/theme/colors";
import { CaseTable } from "@/features/case/components/CaseList/CaseTable";
import { usePagination } from "@/hooks/usePagination";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { useCaseList } from "@/api/services/main/case";
import { TGetCaseListReqDto } from "@/api/services/main/case/dtos/get-case-list.dto";
import { CaseListTop } from "@/features/case/components/CaseList/CaseListTop";
import { Pagination } from "@/components/elements/Pagination";
import {
  CASE_LIST_ADVANCED_FILTERS_STORAGE_KEY,
  CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY,
  CASE_LIST_APPLIED_CONDITION_NAME_STORAGE_KEY,
  CASE_LIST_APPLIED_CONDITION_ID_STORAGE_KEY,
  CASE_LIST_SORT_STORAGE_KEY,
  CASE_LIST_DEFAULT_SEEDED_STORAGE_KEY,
} from "@/features/case/constants/storage-keys";
import { useSanitizedSortParams } from "@/hooks/useSanitizedSortParams";
import { useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { CASE_SORTABLE_FIELDS } from "@/constants/sortable-fields";
import { AppRoutes } from "@/routes/config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/elements/Button";
import { IconLaptopSearch } from "@/assets/icons/IconLaptopSearch";
import { Box } from "@mui/material";
import {
  TCaseListFilterFormExtended,
  TCaseListDisplayColumnSetting,
  defaultCaseListFilterValues,
  defaultCaseListDisplayColumns,
  stripEmptyCaseListFilterFields,
  isEmptyFilterValue,
  normalizeMultiSelectFields,
  normalizeCaseDateFields,
  LOCKED_CASE_LIST_COLUMN_IDS,
} from "@/features/case/components/CaseList/case-list-filter-shared";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";
import { enforceLockedColumns, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { useSavedConditionList } from "@/api/services/main/saved-condition";
import { readSessionFilterState, writeSessionFilterState } from "@/utils/session-filter-state";

export const CaseListPage = () => {
  const pagReq = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const { provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();
  const sortInitializedRef = useRef(false);

  useSanitizedPageParam({ basePath: AppRoutes.caseList });
  const { sortValue } = useSanitizedSortParams({
    allowedFields: [...CASE_SORTABLE_FIELDS],
  });

  // Restore sort from sessionStorage on mount if URL has no sorts param
  useEffect(() => {
    if (sortInitializedRef.current) return;
    sortInitializedRef.current = true;

    if (!searchParams.get("sorts")) {
      const storedSort = sessionStorage.getItem(CASE_LIST_SORT_STORAGE_KEY);
      if (storedSort) {
        const params = new URLSearchParams(searchParams);
        params.set("sorts", storedSort);
        setSearchParams(params, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (sortValue) {
      sessionStorage.setItem(CASE_LIST_SORT_STORAGE_KEY, sortValue);
    } else {
      sessionStorage.removeItem(CASE_LIST_SORT_STORAGE_KEY);
    }
  }, [sortValue]);

  // Applied condition name/id state (localStorage for cross-session persistence)
  const [appliedConditionName, setAppliedConditionName] = useState<string>(() => {
    return localStorage.getItem(CASE_LIST_APPLIED_CONDITION_NAME_STORAGE_KEY) ?? "";
  });
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() => {
    const saved = localStorage.getItem(CASE_LIST_APPLIED_CONDITION_ID_STORAGE_KEY);
    if (saved) {
      const parsed = Number(saved);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  });

  const handleAppliedConditionNameChange = useCallback((name: string) => {
    setAppliedConditionName(name);
    if (name) {
      localStorage.setItem(CASE_LIST_APPLIED_CONDITION_NAME_STORAGE_KEY, name);
    } else {
      localStorage.removeItem(CASE_LIST_APPLIED_CONDITION_NAME_STORAGE_KEY);
    }
  }, []);

  const handleAppliedConditionIdChange = useCallback((id: number | null) => {
    setAppliedConditionId(id);
    if (id !== null) {
      localStorage.setItem(CASE_LIST_APPLIED_CONDITION_ID_STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(CASE_LIST_APPLIED_CONDITION_ID_STORAGE_KEY);
    }
  }, []);

  // Advanced filters state (localStorage)
  const [advancedFilters, setAdvancedFilters] = useState<TCaseListFilterFormExtended | null>(() => {
    const saved = localStorage.getItem(CASE_LIST_ADVANCED_FILTERS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...defaultCaseListFilterValues,
          ...normalizeMultiSelectFields(parsed),
          ...normalizeCaseDateFields(parsed),
        } as TCaseListFilterFormExtended;
      } catch {
        return null;
      }
    }
    return null;
  });

  const resetToFirstPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(START_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TCaseListFilterFormExtended) => {
      const hasAnyFilter = Object.values(filters).some((v) => !isEmptyFilterValue(v));
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        localStorage.setItem(CASE_LIST_ADVANCED_FILTERS_STORAGE_KEY, JSON.stringify(stripEmptyCaseListFilterFields(filters)));
      } else {
        setAdvancedFilters(null);
        localStorage.removeItem(CASE_LIST_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  // Display column settings state (localStorage)
  const [displayColumnSettings, setDisplayColumnSettings] = useState<TCaseListDisplayColumnSetting[] | null>(() => {
    const saved = localStorage.getItem(CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed: TCaseListDisplayColumnSetting[] = JSON.parse(saved);
        // Sync with default columns: append any missing columns
        const missing = defaultCaseListDisplayColumns.filter((d: TCaseListDisplayColumnSetting) => !parsed.some((p: TCaseListDisplayColumnSetting) => p.id === d.id));
        if (missing.length > 0) {
          const merged = [...parsed, ...missing].map((col, idx) => ({ ...col, order: idx + 1 }));
          localStorage.setItem(CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(merged));
          return merged;
        }
        return parsed;
      } catch {
        return null;
      }
    }
    return null;
  });
  const lockedDisplayColumnSettings = useMemo(
    () => displayColumnSettings && enforceLockedColumns(displayColumnSettings, LOCKED_CASE_LIST_COLUMN_IDS),
    [displayColumnSettings],
  );

  const handleApplyDisplaySettings = useCallback((columns: TCaseListDisplayColumnSetting[]) => {
    setDisplayColumnSettings(columns);
  }, []);

  const { data: savedConditionsData } = useSavedConditionList("case");
  const hasSeededDefault = useRef(readSessionFilterState<boolean>(CASE_LIST_DEFAULT_SEEDED_STORAGE_KEY) ?? false);

  useEffect(() => {
    if (hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(CASE_LIST_DEFAULT_SEEDED_STORAGE_KEY, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    const filters = {
      ...defaultCaseListFilterValues,
      ...normalizeMultiSelectFields(normalizeCaseDateFields(fallback.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TCaseListFilterFormExtended;
    setAdvancedFilters(filters);
    localStorage.setItem(CASE_LIST_ADVANCED_FILTERS_STORAGE_KEY, JSON.stringify(stripEmptyCaseListFilterFields(filters)));
    handleAppliedConditionNameChange(fallback.title);
    handleAppliedConditionIdChange(Number(fallback.id));
    const seededColumns = resolveSavedConditionColumns(fallback, defaultCaseListDisplayColumns);
    setDisplayColumnSettings(seededColumns);
    localStorage.setItem(CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(seededColumns));
  }, [savedConditionsData]);

  // Convert province/municipality codes to names for API filtering (multi-select)
  const resolveProvinceNames = (codes: string[]): string[] => {
    if (!codes || codes.length === 0) return [];
    return codes
      .map((c) => provinceOptions.find((opt) => opt.value === c)?.label)
      .filter((v): v is string => !!v);
  };

  const resolveMunicipalityNames = (provinceCodes: string[], municipalityCodes: string[]): string[] => {
    if (!provinceCodes || provinceCodes.length === 0) return [];
    if (!municipalityCodes || municipalityCodes.length === 0) return [];
    const nameSet = new Set<string>();
    for (const provinceCode of provinceCodes) {
      const opts = getMunicipalityOptionsByProvince(provinceCode);
      for (const municipalityCode of municipalityCodes) {
        const label = opts.find((opt) => opt.value === municipalityCode)?.label;
        if (label) nameSet.add(label);
      }
    }
    return Array.from(nameSet);
  };

  // Build API query from advanced filters
  const query: TGetCaseListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: pagReq.perPage || DEFAULT_PER_PAGE,
    ...(advancedFilters && {
      ...(!isEmptyFilterValue(advancedFilters.caseStatus) ? { caseStatus: advancedFilters.caseStatus } : {}),
      ...(advancedFilters.managementNumber ? { managementNumber: advancedFilters.managementNumber } : {}),
      ...(advancedFilters.caseName ? { caseName: advancedFilters.caseName } : {}),
      ...(advancedFilters.jobName ? { jobName: advancedFilters.jobName } : {}),
      ...(!isEmptyFilterValue(advancedFilters.companyName) ? { companyName: advancedFilters.companyName } : {}),
      ...(!isEmptyFilterValue(advancedFilters.companySpecificIndustryId) ? { companySpecificIndustryId: advancedFilters.companySpecificIndustryId } : {}),
      ...((() => {
        const names = resolveProvinceNames(advancedFilters.workplaceProvinceCode);
        return names.length > 0 ? { workplaceProvince: names } : {};
      })()),
      ...((() => {
        const names = resolveMunicipalityNames(advancedFilters.workplaceProvinceCode, advancedFilters.workplaceMunicipalityCode);
        return names.length > 0 ? { workplaceMunicipality: names } : {};
      })()),
      ...(!isEmptyFilterValue(advancedFilters.recruitmentNumber) ? { recruitmentNumberRange: advancedFilters.recruitmentNumber } : {}),
      ...(!isEmptyFilterValue(advancedFilters.caseMatchCount) ? { caseMatchCountRange: advancedFilters.caseMatchCount } : {}),
      ...(!isEmptyFilterValue(advancedFilters.personInChargeId) ? { personInChargeId: advancedFilters.personInChargeId } : {}),
      ...(advancedFilters.registrationDateFrom ? { registrationDateFrom: advancedFilters.registrationDateFrom.format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.registrationDateTo ? { registrationDateTo: advancedFilters.registrationDateTo.format("YYYY-MM-DD") } : {}),
    }),
  };

  if (sortValue) {
    query.sorts = [sortValue as `${string}|${string}`];
  }

  const { data, isLoading } = useCaseList(query);

  return (
    <Stack>
      <Stack mb={3} justifyContent="space-between" alignItems="center" direction="row" width="100%">
        <TopPage title="案件管理" enablePageBack={false} />
        <Box display="flex" gap={2}>
          <Button variant="outline" component={Link} to={AppRoutes.jobList} size="sm" startIcon={<IconLaptopSearch />} sx={{ width: "158px" }}>
            <Typography variant="body14Semi">求人一覧を確認</Typography>
          </Button>
        </Box>
      </Stack>
      <InnerBox
        padding={3}
        bordered={true}
        borderColor={divider.high}
        showDivider={false}
      >
        <Stack>
          <CaseListTop
            pagination={data?.pagination}
            onApplyAdvancedFilters={handleApplyAdvancedFilters}
            onApplyDisplaySettings={handleApplyDisplaySettings}
            appliedConditionName={appliedConditionName}
            appliedConditionId={appliedConditionId}
            onAppliedConditionNameChange={handleAppliedConditionNameChange}
            onAppliedConditionIdChange={handleAppliedConditionIdChange}
            currentDisplayColumns={lockedDisplayColumnSettings}
            currentAdvancedFilters={advancedFilters}
          />
          <Stack spacing={3}>
            <CaseTable rows={data?.data ?? []} isLoading={isLoading} displayColumnSettings={lockedDisplayColumnSettings} />
            <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
          </Stack>
        </Stack>
      </InnerBox>
    </Stack>
  );
};
